import React, { useContext, useState, useEffect, useRef, Suspense } from 'react'
import ContentStack from 'src/api/contentStack'
import { ApiContent, IS_BROWSER } from 'src/utils'
import { RemoteLoader } from './RemoteLoader'
import { ErrorBoundary } from 'src/views/components'
import {
  microFrontendConfigFromCS,
  mfeAppKey,
  buildMfeLoaderId,
  normalizePath,
  isSamePath,
  isMfeServerDataAvailable,
  deserializeSeoTags,
} from './MicroFrontendUtils'
import { Helmet } from 'react-helmet'

function MicroFrontendLoader(props) {
  const context = useContext(ApiContent)
  const appName = props?.remoteConfig?.appName
  const component = props?.remoteConfig?.component || 'App'
  const appKey = mfeAppKey(appName, component)

  const loaderId =
    props?.loaderId ??
    buildMfeLoaderId({ index: props?.loaderIndex, appName, component })

  // Capture server-rendered HTML before React's first paint mutates the DOM.
  // useState lazy initializer runs synchronously — equivalent to the constructor
  // in the former class component.
  const [initialSSRHtml] = useState(() => {
    if (!IS_BROWSER) return ''
    try {
      const node = document?.querySelector?.(
        `[data-mfe-loader-id="${loaderId}"]`
      )
      return node?.innerHTML || ''
    } catch {
      return ''
    }
  })

  // Initialise mfeAppData from context before the first render.
  const [mfeAppData, setMfeAppData] = useState(() => {
    if (!IS_BROWSER) {
      // Server: populated by getDataFromTree (see getDataFromTree.js)
      return context?.mfeAppsMap?.[appKey]?.mfeServerData || {}
    }
    // Browser: use SSR data only when the path matches — guards against stale
    // data on client-side navigations.
    // Browser: hydrated from window.__initialData.mfeServerDataMap
    // (written in getHtml.js, with ssrHtml stripped to keep payload size down).
    // seoTags stay in that payload so the client can keep <title>/<meta> in
    // Helmet until the live MFE getSEOTags is loaded.

    const serverData = context?.mfeServerDataMap?.[appKey] || {}
    if (
      isSamePath(serverData?._hostMeta?.url, window?.location?.pathname || '')
    ) {
      return serverData
    }
    return {}
  })

  // Initialise remoteConfig from the SSR-provided client config immediately on
  // hydration to skip the ContentStack round-trip on first paint.
  const [remoteConfig, setRemoteConfig] = useState(() => {
    if (!IS_BROWSER) return {}
    const serverData = context?.mfeServerDataMap?.[appKey] || {}
    const clientConfig = serverData?._clientRemoteConfig
    if (
      clientConfig?.name &&
      clientConfig?.entry &&
      (!appName || clientConfig.name === appName)
    ) {
      return clientConfig
    }
    return {}
  })

  // Server-side init: trigger loadMfeConfig for MFEs not pre-fetched by mfeServer.js.
  // NOTE: Do not move this into a useEffect — useEffect does not run during SSR.
  // The ref gates it to a single execution per component instance.
  const serverInitDoneRef = useRef(false)
  const hasLoggedSeoMissingRef = useRef(false)
  if (!IS_BROWSER && !serverInitDoneRef.current) {
    serverInitDoneRef.current = true
    if (!context?.mfeAppsMap?.[appKey]?.mfeServerData) {
      context?.loadMfeConfig({
        remoteConfig: props?.remoteConfig || {},
        isToEnableSSR: props?.isToEnableSSR || false,
        params: props?.params || {},
      })
    }
  }

  // Browser init: fetch MFE config from ContentStack.
  // Skips the round-trip when SSR already provided a resolved client remote config.
  useEffect(() => {
    if (!IS_BROWSER) return
    initOnClient()
  }, [])

  const initOnClient = async () => {
    // Skip redundant ContentStack call when remoteConfig is already resolved from SSR.
    if (remoteConfig?.name && remoteConfig?.entry) return
    try {
      const response = await ContentStack.getReference(
        'microfrontend_config',
        '',
        props,
        { isToSkipIncludes: true, includeFallback: true }
      )
      const resolved = microFrontendConfigFromCS({
        response,
        applicationName: appName,
        component,
      })
      if (resolved?.name && resolved?.entry) {
        setRemoteConfig(resolved)
      }
    } catch (error) {
      console.error('MicroFrontendLoader: Error loading MFE config', error)
    }
  }

  const isServerDataAvailable = isMfeServerDataAvailable(mfeAppData)

  const isToEnableSSR = props?.isToEnableSSR || false

  // Extracts the server render decision tree into a dedicated function so the
  // main return block reads linearly.
  const renderServerSide = () => {
    if (mfeAppData?._routing?.pageStatus === 'not_found') return null

    if (mfeAppData?.props?.ssrHtml) {
      // SEO tags arrive as serialized { type, props } descriptors from the
      // child SSR process (functions cannot cross the process boundary).
      // Re-render them into <Helmet> so Helmet.renderStatic() picks them up.
      // seoTags are also kept in __initialData for the client RemoteLoader
      // fallback until live getSEOTags is available.
      let seoTagElements = []
      try {
        seoTagElements = deserializeSeoTags(mfeAppData?.props?.seoTags)
      } catch (error) {
        console.error('[MFE SSR] deserializeSeoTags failed', error)
      }
      if (
        !Array.isArray(mfeAppData?.props?.seoTags) &&
        process.env.NODE_ENV !== 'production' &&
        !hasLoggedSeoMissingRef.current
      ) {
        hasLoggedSeoMissingRef.current = true
        console.debug(
          '[MFE SSR] seoTags not present — client navigation path, SEO <head> tags not re-applied for',
          loaderId
        )
      }
      return (
        <ErrorBoundary>
          <Suspense fallback={null}>
            {seoTagElements.length > 0 && <Helmet>{seoTagElements}</Helmet>}
            <div
              data-mfe-loader-id={loaderId}
              suppressHydrationWarning
              dangerouslySetInnerHTML={{ __html: mfeAppData.props.ssrHtml }}
            />
          </Suspense>
        </ErrorBoundary>
      )
    }

    const perAppEntry = context?.mfeAppsMap?.[appKey]
    const {
      mfeComponent: MicroFrontEndServerComponent,
      getSEOTags,
      params: mfeParams,
    } = perAppEntry || {}

    if (!MicroFrontEndServerComponent) return null

    const ssrParams = { ...mfeAppData?.props, ...props?.params }
    return (
      <ErrorBoundary>
        <Suspense fallback={null}>
          <div data-mfe-loader-id={loaderId}>
            {getSEOTags && (
              <Helmet>
                {getSEOTags({
                  params: mfeParams || ssrParams,
                  serverProps: mfeAppData,
                  isServerDataAvailable: true,
                })}
              </Helmet>
            )}
            <MicroFrontEndServerComponent params={ssrParams} />
          </div>
        </Suspense>
      </ErrorBoundary>
    )
  }

  // Extracts the client render decision tree into a dedicated function so the
  // main return block reads linearly — mirrors renderServerSide().
  // The data-mfe-loader-id container is rendered by RemoteLoader (or inline
  // below) as the SAME element the server emitted, so hydration of an SSR'd
  // MFE sees no extra wrapper. The <Suspense> boundary mirrors the server one
  // and confines any MFE-internal hydration mismatch to this island.
  const renderClientSide = () => {
    if (remoteConfig.name && remoteConfig.entry) {
      return (
        <ErrorBoundary>
          <Suspense fallback={null}>
            <RemoteLoader
              remoteConfig={remoteConfig}
              componentParams={{ ...mfeAppData?.props, ...props?.params }}
              routing={mfeAppData?._routing}
              isServerDataAvailable={isServerDataAvailable}
              ssrFallbackHtml={initialSSRHtml}
              seoTags={mfeAppData?.props?.seoTags}
              onPageNotFound={props?.onPageNotFound}
              fallback={props?.fallback}
              loaderId={loaderId}
            />
          </Suspense>
        </ErrorBoundary>
      )
    }

    // remoteConfig not yet resolved — keep server-rendered HTML in place until
    // the ContentStack lookup completes on the client. On pure CSR navigations
    // where no SSR HTML exists, fall back to the host-supplied placeholder so
    // the container isn't blank while ContentStack + remoteEntry.js are still
    // in flight.
    const fallbackMarkup = isServerDataAvailable ? initialSSRHtml : ''
    if (!fallbackMarkup && props?.fallback) {
      return (
        <ErrorBoundary>
          <Suspense fallback={null}>
            <div data-mfe-loader-id={loaderId}>{props.fallback}</div>
          </Suspense>
        </ErrorBoundary>
      )
    }
    return (
      <ErrorBoundary>
        <Suspense fallback={null}>
          <div
            data-mfe-loader-id={loaderId}
            suppressHydrationWarning
            dangerouslySetInnerHTML={
              fallbackMarkup ? { __html: fallbackMarkup } : undefined
            }
          />
        </Suspense>
      </ErrorBoundary>
    )
  }

  if (IS_BROWSER) return renderClientSide()
  if (isToEnableSSR) return renderServerSide()
  return null
}

export default MicroFrontendLoader
export { MicroFrontendLoader }
